import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Spinner, Alert, Image, Badge, Pagination, Form, InputGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FaCheck, FaTrash, FaTimes, FaSearch } from 'react-icons/fa';
import { useUserAuth } from '../../context/UserAuthContext';
import axios from 'axios';
import './Admin.css';

const API_BASE_URI = process.env.REACT_APP_API_BASE_URI;

const PhotoManagement = () => {
  const [photos, setPhotos] = useState([]);
  const [pendingPhotos, setPendingPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activePage, setActivePage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('all'); // 'all' or 'pending'
  const { user } = useUserAuth();
  const { t } = useTranslation();
  const photosPerPage = 10;

  useEffect(() => {
    fetchPhotos();
    fetchPendingPhotos();
  }, []);

  const fetchPhotos = async () => {
    try {
      setLoading(true);
      setError('');

      const token = await user.getIdToken();
      const headers = { Authorization: `Bearer ${token}` };

      const response = await axios.get(`${API_BASE_URI}/admin/photos?limit=100`, { headers });
      
      setPhotos(response.data.photos || []);
      
      // Calculate total pages
      const total = response.data.photos?.length || 0;
      setTotalPages(Math.ceil(total / photosPerPage));
      
    } catch (err) {
      console.error('Error fetching photos:', err);
      setError('Failed to load photos');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingPhotos = async () => {
    try {
      const token = await user.getIdToken();
      const headers = { Authorization: `Bearer ${token}` };

      const response = await axios.get(`${API_BASE_URI}/admin/photos/pending`, { headers });
      
      setPendingPhotos(response.data.photos || []);
      
    } catch (err) {
      console.error('Error fetching pending photos:', err);
      // We don't set the main error here to avoid disrupting the UI if only this request fails
    }
  };

  const handleApprovePhoto = async (photoId) => {
    try {
      const token = await user.getIdToken();
      const headers = { Authorization: `Bearer ${token}` };

      await axios.patch(
        `${API_BASE_URI}/admin/photos/${photoId}/approve`, 
        { approved: true },
        { headers }
      );

      // Update pending photos list
      setPendingPhotos(pendingPhotos.filter(photo => photo.id !== photoId));
      
      // Refresh all photos
      fetchPhotos();
      
    } catch (err) {
      console.error('Error approving photo:', err);
      setError('Failed to approve photo');
    }
  };

  const handleRejectPhoto = async (photoId) => {
    if (window.confirm(t('adminDashboard.confirmRejectPhoto'))) {
      try {
        const token = await user.getIdToken();
        const headers = { Authorization: `Bearer ${token}` };

        await axios.patch(
          `${API_BASE_URI}/admin/photos/${photoId}/approve`, 
          { approved: false },
          { headers }
        );

        // Update pending photos list
        setPendingPhotos(pendingPhotos.filter(photo => photo.id !== photoId));
        
        // Update all photos list
        setPhotos(photos.filter(photo => photo.id !== photoId));
        
      } catch (err) {
        console.error('Error rejecting photo:', err);
        setError('Failed to reject photo');
      }
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (window.confirm(t('adminDashboard.confirmDeletePhoto'))) {
      try {
        const token = await user.getIdToken();
        const headers = { Authorization: `Bearer ${token}` };

        await axios.delete(`${API_BASE_URI}/photos/${photoId}`, { headers });

        // Update both lists
        setPendingPhotos(pendingPhotos.filter(photo => photo.id !== photoId));
        setPhotos(photos.filter(photo => photo.id !== photoId));
        
      } catch (err) {
        console.error('Error deleting photo:', err);
        setError('Failed to delete photo');
      }
    }
  };

  // Filter photos based on search term
  const filteredPhotos = photos.filter(photo => 
    photo.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (photo.description && photo.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Get current page of photos
  const currentPhotos = filteredPhotos.slice(
    (activePage - 1) * photosPerPage,
    activePage * photosPerPage
  );

  // Create pagination items
  const paginationItems = [];
  for (let number = 1; number <= totalPages; number++) {
    paginationItems.push(
      <Pagination.Item 
        key={number} 
        active={number === activePage}
        onClick={() => setActivePage(number)}
      >
        {number}
      </Pagination.Item>
    );
  }

  return (
    <div className="photo-management">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>{t('adminDashboard.photoManagement')}</h2>
        <div>
          <Button 
            variant={viewMode === 'all' ? 'primary' : 'outline-primary'} 
            className="me-2"
            onClick={() => setViewMode('all')}
          >
            {t('adminDashboard.allPhotos')} 
            <Badge bg="light" text="dark" className="ms-2">{photos.length}</Badge>
          </Button>
          <Button 
            variant={viewMode === 'pending' ? 'warning' : 'outline-warning'} 
            onClick={() => setViewMode('pending')}
          >
            {t('adminDashboard.pendingApproval')} 
            <Badge bg="light" text="dark" className="ms-2">{pendingPhotos.length}</Badge>
          </Button>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {viewMode === 'pending' && pendingPhotos.length === 0 && (
        <Alert variant="info">{t('adminDashboard.noPendingPhotos')}</Alert>
      )}

      {viewMode === 'pending' && pendingPhotos.length > 0 && (
        <Card className="mb-4">
          <Card.Header>{t('adminDashboard.pendingPhotos')}</Card.Header>
          <Card.Body>
            <div className="pending-photos-grid">
              {pendingPhotos.map(photo => (
                <div key={photo.id} className="pending-photo-card">
                  <Image 
                    src={photo.imageUrl} 
                    alt={photo.description || 'Pending photo'} 
                    thumbnail
                    className="pending-photo-img"
                  />
                  <div className="pending-photo-details">
                    <p><strong>{photo.userName}</strong></p>
                    <p className="small text-muted">
                      {new Date(photo.uploadedAt).toLocaleDateString()}
                    </p>
                    <p className="pending-photo-description">
                      {photo.description || t('adminDashboard.noDescription')}
                    </p>
                  </div>
                  <div className="pending-photo-actions">
                    <Button 
                      variant="success" 
                      size="sm" 
                      className="me-2"
                      onClick={() => handleApprovePhoto(photo.id)}
                    >
                      <FaCheck />
                    </Button>
                    <Button 
                      variant="danger" 
                      size="sm"
                      onClick={() => handleRejectPhoto(photo.id)}
                    >
                      <FaTimes />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </Card.Body>
        </Card>
      )}

      {viewMode === 'all' && (
        <>
          <Card>
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                {t('adminDashboard.allPhotos')}
                <InputGroup className="search-input">
                  <InputGroup.Text>
                    <FaSearch />
                  </InputGroup.Text>
                  <Form.Control
                    placeholder={t('adminDashboard.searchPhotos')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </div>
            </Card.Header>
            <Card.Body>
              {loading ? (
                <div className="text-center py-3">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">{t('loading')}</span>
                  </Spinner>
                </div>
              ) : currentPhotos.length === 0 ? (
                <Alert variant="info">
                  {searchTerm 
                    ? t('adminDashboard.noPhotoMatchSearch') 
                    : t('adminDashboard.noPhotosYet')
                  }
                </Alert>
              ) : (
                <div className="table-responsive">
                  <Table striped hover>
                    <thead>
                      <tr>
                        <th>{t('adminDashboard.image')}</th>
                        <th>{t('adminDashboard.user')}</th>
                        <th>{t('adminDashboard.date')}</th>
                        <th>{t('adminDashboard.likes')}</th>
                        <th>{t('adminDashboard.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentPhotos.map(photo => (
                        <tr key={photo.id}>
                          <td>
                            <Image 
                              src={photo.imageUrl} 
                              alt={photo.description || 'Photo'} 
                              width={80} 
                              height={60}
                              className="admin-thumbnail"
                            />
                          </td>
                          <td>{photo.userName}</td>
                          <td>{new Date(photo.uploadedAt).toLocaleDateString()}</td>
                          <td>{photo.likeCount || 0}</td>
                          <td>
                            <Button 
                              variant="danger" 
                              size="sm"
                              onClick={() => handleDeletePhoto(photo.id)}
                            >
                              <FaTrash />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
            <Card.Footer>
              <Pagination className="mb-0 justify-content-center">
                <Pagination.First onClick={() => setActivePage(1)} disabled={activePage === 1} />
                <Pagination.Prev onClick={() => setActivePage(Math.max(1, activePage - 1))} disabled={activePage === 1} />
                {paginationItems}
                <Pagination.Next onClick={() => setActivePage(Math.min(totalPages, activePage + 1))} disabled={activePage === totalPages} />
                <Pagination.Last onClick={() => setActivePage(totalPages)} disabled={activePage === totalPages} />
              </Pagination>
            </Card.Footer>
          </Card>
        </>
      )}
    </div>
  );
};

export default PhotoManagement;